import * as React from 'react'
import moment from 'moment'
import { faClose, FaIcon } from '@/lib/fa'
import ModalLayout from '../shared/ModalLayout'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { axiosInstance } from '@/modules/shared/lib/axios'
import usePrintService from '@/modules/drivers/services/PrintService'
import { usePrintJobGuard } from '@/modules/printouts/hooks/usePrintJobGuard'
import { ReportType } from '@/app/constants/reportType'
import { TaskType } from '@/app/constants/taskType'
import { SERVICE_TIME_FORMAT } from '@/app/constants/date'
import { toast } from '@/lib/toasts/api'
import { useTask } from '@/modules/shared/context/TaskContext'
import { Button } from '@/app/features/common/Button'

type GroupedContractPrintDocumentType = 'base' | 'annex'

type ApiContractRow = {
  id?: string | number
  isAnnex?: boolean
  startDate?: string
}

const pickNewestContract = (
  contracts: ApiContractRow[],
  wantAnnex: boolean,
): ApiContractRow | null => {
  const filtered = contracts.filter(
    (c) =>
      c != null &&
      c.id != null &&
      (wantAnnex ? Boolean(c.isAnnex) : !c.isAnnex),
  )
  if (filtered.length === 0) {
    return null
  }
  return [...filtered].sort((a, b) => {
    const t =
      new Date(b.startDate ?? 0).getTime() -
      new Date(a.startDate ?? 0).getTime()
    if (t !== 0) {
      return t
    }
    const idA = Number(a.id)
    const idB = Number(b.id)
    return idB - idA
  })[0]
}

type Props = {
  isOpen: boolean
  onClose: () => void
  companyId: string
  driverIds: number[]
}

const PrintGroupedContractTypeModal = ({
  isOpen,
  onClose,
  companyId,
  driverIds,
}: Props) => {
  const { t } = useLocalize()
  const { getPdf } = usePrintService()
  const { addTask } = useTask()
  const [documentType, setDocumentType] =
    React.useState<GroupedContractPrintDocumentType>('base')
  const [isBusy, setIsBusy] = React.useState(false)
  const { isGenerating, runPrintJob } = usePrintJobGuard()
  const isPending = isBusy || isGenerating

  React.useEffect(() => {
    if (isOpen) {
      setDocumentType('base')
      setIsBusy(false)
    }
  }, [isOpen])

  const printContracts = React.useCallback(async () => {
    if (isBusy || driverIds.length === 0) return

    const wantAnnex = documentType === 'annex'
    setIsBusy(true)

    const startTime = moment()
      .subtract(1, 'year')
      .startOf('day')
      .format(SERVICE_TIME_FORMAT)
    const endTime = moment().endOf('day').format(SERVICE_TIME_FORMAT)

    try {
      const results = await Promise.all(
        driverIds.map(async (driverId) => {
          try {
            const res = await axiosInstance.get<ApiContractRow[]>(
              `/api/${companyId}/contract?driverId=${driverId}`,
            )
            const list = Array.isArray(res.data) ? res.data : []
            const chosen = pickNewestContract(list, wantAnnex)
            if (!chosen?.id) {
              return { driverId, contractId: null as string | null }
            }
            return { driverId, contractId: String(chosen.id) }
          } catch {
            return { driverId, contractId: null as string | null }
          }
        }),
      )

      const toPrint = results.filter((r) => r.contractId != null) as Array<{
        driverId: number
        contractId: string
      }>
      const skipped = results.length - toPrint.length

      if (toPrint.length === 0) {
        toast.warning({
          text: wantAnnex
            ? t('printout.groupedContractPrintNoneAnnex')
            : t('printout.groupedContractPrintNoneBase'),
        })
        return
      }

      for (const { driverId, contractId } of toPrint) {
        const taskId = await getPdf({
          companyId,
          reportType: ReportType.DRIVER_CONTRACT_ADAPTIVE,
          drivers: [driverId],
          startTime,
          endTime,
          data: { separateFiles: true, contractId },
          showLoading: false,
        })
        addTask(taskId, TaskType.Printout, ReportType.DRIVER_CONTRACT_ADAPTIVE)
      }

      if (skipped > 0) {
        toast.info({
          text: t('printout.groupedContractPrintPartial')
            .replace('{0}', String(toPrint.length))
            .replace('{1}', String(skipped)),
        })
      }

      onClose()
    } catch (error) {
      console.error('Error printing grouped contracts:', error)
      toast.error({ text: t('printout.generation-error') })
    } finally {
      setIsBusy(false)
    }
  }, [
    isBusy,
    driverIds,
    documentType,
    companyId,
    getPdf,
    addTask,
    t,
    onClose,
  ])

  const handlePrint = React.useCallback(
    () => runPrintJob(printContracts),
    [printContracts, runPrintJob],
  )

  if (!isOpen) {
    return null
  }

  return (
    <ModalLayout customZIndex={9999}>
      <div className="tw-w-full tw-max-w-[520px] tw-overflow-hidden tw-rounded-xl tw-bg-base-100 tw-shadow-multi">
        <div className="tw-ring-b tw-ring-base-100">
          <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-4">
            <h2 className="tw-m-0 tw-text-xl tw-font-semibold tw-text-content-base">
              {t('printout.groupedContractPrintTitle')}
            </h2>
            <FaIcon
              icon={faClose}
              className="tw-h-5 tw-w-5 tw-shrink-0 tw-cursor-pointer tw-text-content-base hover:tw-opacity-70"
              aria-hidden
              onClick={() => !isBusy && onClose()}
            />
          </div>
        </div>

        <p className="tw-m-0 tw-px-4 tw-pt-1 tw-pb-2 tw-text-sm tw-leading-relaxed tw-text-content-base">
          {t('printout.groupedContractPrintDescription')}
        </p>

        <div className="tw-flex tw-flex-col tw-gap-3 tw-px-4 tw-py-4">
          <label className="tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-lg tw-p-2 tw-pl-2 hover:tw-bg-base-200/60">
            <input
              type="radio"
              name="grouped-contract-print-type"
              className="tw-d-radio tw-d-radio-primary"
              checked={documentType === 'base'}
              disabled={isBusy}
              onChange={() => setDocumentType('base')}
            />
            <span className="tw-select-none tw-text-sm tw-font-medium tw-leading-snug tw-text-content-base">
              {t('printout.groupedContractPrintOptionBase')}
            </span>
          </label>
          <label className="tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-lg tw-p-2 tw-pl-2 hover:tw-bg-base-200/60">
            <input
              type="radio"
              name="grouped-contract-print-type"
              className="tw-d-radio tw-d-radio-primary"
              checked={documentType === 'annex'}
              disabled={isBusy}
              onChange={() => setDocumentType('annex')}
            />
            <span className="tw-select-none tw-text-sm tw-font-medium tw-leading-snug tw-text-content-base">
              {t('printout.groupedContractPrintOptionAnnex')}
            </span>
          </label>
        </div>

        <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-border-t tw-border-base-300 tw-px-4 tw-py-4">
          {isPending && (
            <span className="tw-text-xs tw-text-content-base/70">
              {t('printout.generation-in-progress')}
            </span>
          )}
          <Button
            type="button"
            variant="secondary"
            size="md"
            disabled={isBusy}
            text={t('common.cancel')}
            onClick={onClose}
            className="tw-min-w-[7rem]"
          />
          <Button
            type="button"
            variant="primary"
            size="md"
            disabled={isPending || driverIds.length === 0}
            text={isPending ? t('common.loading') : t('common.print')}
            onClick={() => void handlePrint()}
            className="tw-min-w-[7rem]"
          />
        </div>
      </div>
    </ModalLayout>
  )
}

export default PrintGroupedContractTypeModal
